import { Table, Badge, Button, UncontrolledTooltip, Input } from "reactstrap";
import { withNamespaces } from "react-i18next";
import DateUtils from 'src/services/utils/DateUtils';
import { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import moment from "moment";
import { Accordion } from "react-bootstrap";
import ManagementTeamImage from "src/modules/3rd-party-management/assets/images/management-team.svg";
import { RiCheckLine, RiCloseLine, RiInformationLine } from "react-icons/ri";
import ScreeningConfirmationModal from "../../../../components/screening-confirmation-modal";
import MonitoringConfirmationModal from "../../../../components/monitoring-confirmation-modal";
import { useCallback, useEffect, useState } from "react";
import StopMonitoringConfirmationModal from "../../../../components/stop-monitoring-confirmation-modal";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";

const dateUtils = new DateUtils();

function convertApiDate(dateStr) {
    return dateUtils.convertDateToDate(dateStr, INTERNATIONAL_DATE_FORMAT)
}

const rolesTrans = {
    "managing-director": "Managing Director",
    "power-of-attorney": "Power of Attorney Holder",
    "liquidator": "Liquidator",
    "limited-partner": "Limited Partner",
    "owner": "Owner",
    "personally-liable-partner": "Personally Liable Partner",
    "board-of-directors": "Board of Directors",
    "director": "Director",
    "partner": "Partner",
    "permanent-representative": "Permanent Representative",
    "unwinder": "Company Unwinder",
    "ceo": "Chief Executive Officer (CEO)",
    "executive-director": "Executive Director",
    "emergency-manager": "Emergency Manager",
    "acting-manager": "Acting Manager",
    "supervisory-board": "Supervisory Board Member",
    "shareholder": "Shareholder",
    "deputy-ceo": "Deputy CEO",
    "branch-office-manager": "Branch Office Manager",
    "ip-owner": "Intellectual Property Owner",
    "tenant": "Tenant",
    "member-of-the-board": "Board Member",
    "deputy-general-manager": "Deputy General Manager",
    "chair-of-the-board": "Chair of the Board",
    "other-interest": "Other Interest",
    "secretary": "Secretary",
    "voting-rights": "Voting Rights Holder",
    "president": "President",
    "authorized-representative-body": "Authorized Representative Body",
    "manager": "Manager",
    "general-director": "General Director",
    "delegate-of-the-board": "Board Delegate",
    "authorized-signatory": "Authorized Signatory",
    "auditor": "Auditor",
    "unknown": "Unknown Role",
    "vice-chair-of-the-board": "Vice Chair of the Board"
}

const renderTeam = (team) => {
    const results = [];

    for (const member of team) {
        const memeberRawData = member.rawData || {};

        const memberRoles = memeberRawData.relationships?.organizations?.data || [];

        const roles = memberRoles.flatMap(rel => rel.meta?.roles || []);

        for (const role of roles) {
            const roleType = (role.type || "").toLowerCase();
            const memeberAttributes = memeberRawData.attributes || {};

            results.push({
                id: member.id,
                fullName: `${memeberAttributes.firstName} ${memeberAttributes.lastName}`.trim() || "-",
                dateOfBirth: memeberAttributes.birthDate ? convertApiDate(memeberAttributes.birthDate) : "-",
                birthPlace: `${memeberAttributes.birthPlace?.city || ""}, ${memeberAttributes.birthPlace?.country || ""}`.trim() || "-",
                address: `${memeberAttributes.address?.city || ""}, ${memeberAttributes.address?.country || ""}`.trim() || "-",
                role: roleType,
                roleDateFrom: role.dateFrom ? convertApiDate(role.dateFrom) : '-',
                roleDateTo: role.dateTo ? convertApiDate(role.dateTo) : '-',
                roleIsActive: role.dateTo ? moment(role.dateTo).isAfter(new Date()) : '-',
                pepsStatus: member.pepsStatus || "UNKNOWN",
                sanctionStatus: member.sanctionStatus || "UNKNOWN",
                sanctionHits: member.sanctionHits || 0,
                underMonitoring: !!member.has_monitoring_follow_list_item
            });
        }
    }

    return results;
}

const ManagementTeam = ({
    t,
    emptyMessage = "No team available.",
    activeAccordion,
    setActiveAccordion,
    detailsQuery,
    supplierData
}) => {
    const [ selectedPersonToScreen, setSelectedPersonToScreen ] = useState(null);
    const [ selectedPersonToMonitor, setSelectedPersonToMonitor ] = useState(null);
    const [ selectedRowsForBulkAction, setSelectedRowsForBulkAction ] = useState([]);
    const [ selectedPersonToStopMonitoring, setSelectedPersonToStopMonitoring ] = useState(null);
    const [ showBulkSreenConfirmationModal, setShowBulkScreenConfirmationModal ] = useState(false);

    const [ team, setTeam ] = useState([]);

    const BooleanIcon = ({ value }) => (
        <div className="d-flex align-items-center gap-1">
            {value ? (
                <RiCheckLine size={16} className="text-success" />
            ) : (
                <RiCloseLine size={16} className="text-danger" />
            )}
        </div>
    );

    const statusColor = (status) => {
        switch (status) {
            case "CLEAN":
                return "bg-success";
            case "SANCTIONED":
                return "bg-danger";
            case "UNKNOWN":
                return "bg-secondary";
            default:
                return "bg-warning";
        }
    };

    const handleScreenCompleted = useCallback(() => {
        setSelectedPersonToScreen(null);
    }, [ ]);

    const handleMonitorCompleted = useCallback(() => {
        setTeam((prev) =>
            prev.map((person) => {
                if (person.id === selectedPersonToMonitor?.id) {
                    return {
                        ...person,
                        underMonitoring: true
                    };
                }

                return person;
            })
        );

        setSelectedPersonToMonitor(null);
    }, [ selectedPersonToMonitor ]);

    const handleStopMonitoringCompleted = useCallback(() => {
        setTeam((prev) =>
            prev.map((person) => {
                if (person.id === selectedPersonToStopMonitoring?.id) {
                    return {
                        ...person,
                        underMonitoring: false
                    };
                }

                return person;
            })
        );

        setSelectedPersonToStopMonitoring(null);
    }, [ selectedPersonToStopMonitoring ]);

    useEffect(() => {
        if (detailsQuery.data?.management_team) {
            setTeam(
                renderTeam(detailsQuery.data.management_team)
            )
        }
    }, [ detailsQuery.data ]);

    const onBulkCheckboxSelectAll = useCallback((e) => {
        if (e.target.checked) {
            setSelectedRowsForBulkAction(team.map((item) => item.id));
        } else {
            setSelectedRowsForBulkAction([]);
        }
    }, [ team ]);

    const handlePersonCheckboxCheck = useCallback((e) => {
        const { value, checked } = e.target;
        const id = Number(value);

        setSelectedRowsForBulkAction((prev) => {
            if (checked) {
                return [...prev, id];
            } else {
                return prev.filter((item) => item !== id);
            }
        });
    }, []);

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion('management-team')}
            className={activeAccordion === 'management-team' ? 'active' : ''}
            eventKey={'management-team'}
        >
            <Accordion.Header>
                <img
                    src={ManagementTeamImage}
                    alt=""
                />
                <span>
                    {t('Management Team')}
                </span>
            </Accordion.Header>

            <Accordion.Body>
                <div className="shareholders-section mb-5" role="region" aria-labelledby="shareholders-heading">
                    {team.length === 0 ? (
                        <div className="text-muted small">{emptyMessage}</div>
                    ) : (
                        <div>
                            <div 
                                className="d-flex justify-content-end mb-3"
                            >
                                <span id="bulk-action-start-compliance-screening-btn">
                                    <Button
                                        color="primary"
                                        outline
                                        disabled={!selectedRowsForBulkAction.length}
                                        onClick={() => setShowBulkScreenConfirmationModal(true)}
                                    >
                                        {!selectedRowsForBulkAction.length ? (
                                            <UncontrolledTooltip
                                                target="bulk-action-start-compliance-screening-btn"
                                            >
                                                {t('Select at least one team member to start screening.')}
                                            </UncontrolledTooltip>
                                        ) : null}

                                        {t('Add to Screening')}
                                    </Button>
                                </span>
                            </div>
                            
                            <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                                <Table hover>
                                    <caption className="visually-hidden">
                                        {t('List of management team related to this organization.')}
                                    </caption>
                                    <thead
                                        style={{
                                            position: 'sticky',
                                            top: 0,
                                            background: 'white',
                                            zIndex: 2
                                        }}
                                    >
                                        <tr>
                                            <th
                                                style={{
                                                    width: '25px'
                                                }}
                                            >
                                                <Input
                                                    type="checkbox"
                                                    onChange={onBulkCheckboxSelectAll}
                                                />
                                            </th>
                                            <th>{t('Full Name')}</th>
                                            <th>
                                                {t('Position / Role')}
                                            </th>
                                            <th>
                                                {t('Role Start Date')}
                                            </th>
                                            <th>{t('Role End Date')}</th>
                                            <th>
                                                <span className="me-1">
                                                    {t('Current Role')}
                                                </span>

                                                <RiInformationLine
                                                    id="activeTip"
                                                    size={14}
                                                />
                                                <UncontrolledTooltip
                                                    target="activeTip"
                                                >
                                                    {t('Indicates if the person is currently active')}
                                                </UncontrolledTooltip>
                                            </th>
                                            <th>{t('Date of Birth')}</th>
                                            <th>{t('Place of Birth')}</th>
                                            <th>{t('Address')}</th>
                                            <th>
                                                <span className="me-1">
                                                    {t('PEP Status')}
                                                </span>
                                                
                                                <RiInformationLine
                                                    id="pepTip"
                                                    size={14}
                                                />
                                                <UncontrolledTooltip
                                                    target="pepTip"
                                                >
                                                    {t('Politically Exposed Person status')}
                                                </UncontrolledTooltip>
                                            </th>
                                            <th>
                                                <span className="me-1">
                                                    {t('Sanctions Status')}
                                                </span>
                                                <RiInformationLine
                                                    id="sanctionTip"
                                                    size={14}
                                                />
                                                <UncontrolledTooltip
                                                    target="sanctionTip"
                                                >
                                                    {t('Status on sanctions lists')}
                                                </UncontrolledTooltip>
                                            </th>

                                            <th>{t('Sanctions Matches')}</th>
                                            <th>
                                                {t('Monitoring')}
                                            </th>
                                            <th
                                                style={{
                                                    width: '300px'
                                                }}
                                            >{t('Action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {team.length > 0 ? (
                                            team.map((person, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <Input
                                                            type="checkbox"
                                                            checked={selectedRowsForBulkAction.includes(person.id)}
                                                            onChange={handlePersonCheckboxCheck}
                                                            value={person.id}
                                                        />
                                                    </td>
                                                    <td>
                                                        {person.fullName}
                                                    </td>

                                                    <td>{rolesTrans[person.role] || person.role}</td>
                                                    <td>{person.roleDateFrom}</td>
                                                    <td>{person.roleDateTo}</td>
                                                    <td>
                                                        <BooleanIcon value={person.roleIsActive} />
                                                    </td>
                                                    <td>{person.dateOfBirth}</td>
                                                    <td>{person.birthPlace}</td>
                                                    <td>{person.address}</td>
                                                    <td>
                                                        <Badge className={statusColor(person.pepsStatus)}>
                                                            {t(person.pepsStatus)}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge className={statusColor(person.sanctionStatus)}>
                                                            {t(person.sanctionStatus)}
                                                        </Badge>
                                                    </td>
                                                    <td>{person.sanctionHits}</td>
                                                    <td>
                                                        <span className="d-flex">
                                                            <BooleanIcon
                                                                value={person.underMonitoring}
                                                            />
                                                            {t(person.underMonitoring ? 'Yes' : 'No')}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                color="primary"
                                                                onClick={() => setSelectedPersonToScreen(person)}
                                                            >
                                                                {t('Screen')}
                                                            </Button>

                                                            {isFeatureEnabledForActiveModule('compliance_monitoring_activation') && (
                                                                <>
                                                                    {person.underMonitoring ? (
                                                                        <Button
                                                                            color="warning"
                                                                            onClick={() => setSelectedPersonToStopMonitoring(person)}
                                                                        >
                                                                            {t('Stop Monitoring')}
                                                                        </Button>
                                                                    ) : (
                                                                        <Button
                                                                            color="success"
                                                                            onClick={() => setSelectedPersonToMonitor(person)}
                                                                        >
                                                                            {t('Add to Monitoring')}
                                                                        </Button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="14">
                                                    <div className="text-center">
                                                        <p>{t('No management team members found.')}</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}
                </div>

                <ScreeningConfirmationModal
                    isOpen={!!selectedPersonToScreen || showBulkSreenConfirmationModal}
                    toggle={() => {
                        setSelectedPersonToScreen(null);
                        setShowBulkScreenConfirmationModal(false);
                    }}
                    isBulkAction={showBulkSreenConfirmationModal}
                    supplierId={supplierData?.id}
                    people={showBulkSreenConfirmationModal ? selectedRowsForBulkAction : (selectedPersonToScreen ? [selectedPersonToScreen] : [])}
                    onCompleted={handleScreenCompleted}
                />

                <MonitoringConfirmationModal 
                    isOpen={!!selectedPersonToMonitor}
                    toggle={() => setSelectedPersonToMonitor(null)}
                    person={selectedPersonToMonitor}
                    supplierId={supplierData?.id}
                    onCompleted={handleMonitorCompleted}
                />

                <StopMonitoringConfirmationModal
                    isOpen={!!selectedPersonToStopMonitoring}
                    toggle={() => setSelectedPersonToStopMonitoring(null)}
                    person={selectedPersonToStopMonitoring}
                    onCompleted={handleStopMonitoringCompleted}
                />
            </Accordion.Body>
        </Accordion.Item>
    );
}

export default withNamespaces()(ManagementTeam);
